"use client";

import { useSearchParams, useRouter } from "next/navigation";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Search } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

interface SelectSearchFilterProps {
  items: { value: string | number; label: string }[];
  label: string;
  className?: string;
  placeholder: string;
  paramKey: string;
  disabled?: boolean;
  all?: boolean;
}

const SelectSearchFilter: React.FC<SelectSearchFilterProps> = ({
  items,
  label,
  className,
  placeholder,
  paramKey,
  disabled = false,
  all = false,
}) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const selectedValue = searchParams.get(paramKey) || "";

  const filteredItems = items?.filter((item) =>
    item.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSelect = (value: string | number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value === "" || value === "all") {
      params.delete(paramKey);
    } else {
      params.set(paramKey, value.toString());
    }
    router.push(`?${params.toString()}`);
    setOpen(false);
  };

  React.useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div className="w-full">
          {label && <Label className="font-medium mb-1">{label}</Label>}
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between rounded-full overflow-hidden h-[2.6rem]",
              className
            )}
          >
            <span className="md:text-sm text-xs truncate">
              {selectedValue === "" || selectedValue === "all"
                ? "Semua"
                : items.find((item) => item.value.toString() === selectedValue)
                    ?.label || placeholder}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-full p-0 bg-white"
        style={{ width: triggerWidth ? `${triggerWidth}px` : "auto" }}
      >
        <Command>
          <div className="flex items-center border-b border-[#D9D9D9] px-3">
            <Search className="mr-1 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder={placeholder}
              className="h-9 text-sm placeholder:text-sm pr-4 py-2 w-full focus:outline-none"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <CommandList>
            <CommandEmpty>Data tidak ada</CommandEmpty>
            <CommandGroup>
              {all && (
                <CommandItem
                  value="all"
                  onSelect={() => handleSelect("all")}
                  className="cursor-pointer"
                >
                  Semua
                  <Check
                    className={cn(
                      "ml-auto",
                      selectedValue === "" || selectedValue === "all"
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              )}
              {filteredItems.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value.toString()}
                  onSelect={() => handleSelect(item.value)}
                  className="cursor-pointer"
                >
                  {item.label}
                  <Check
                    className={cn(
                      "ml-auto",
                      selectedValue === item.value.toString()
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SelectSearchFilter;
